/**
 * `TargetRows` — the Overview's "Progress against targets", as the client redrew it on
 * 4 Oct 2026: *one row per country, the country's programme long-term target as one bar
 * beside a stack of that country's yearly actuals, and a key distinguishing the two.*
 *
 * It replaces **both** charts that sat under each target panel — the `BarsH` Target/Achieved
 * pair and the previous day's `StackedColumns` programme-target-against-yearly-targets — with
 * the single chart that answers both of their questions at once. The commitment and the
 * achievement are on one shared value axis across every row, so the distance between the two
 * bars in a row *is* the gap, and the same distance in two rows is the same number of farmers.
 *
 * It is a sibling of `BarsH`, not a mode of it, for the reason `BarsH` is a sibling of
 * `BarsV`: the second bar in a row here is not a second series, it is a **composition** —
 * one segment per reported year, laid end to end — and every prop that would configure it
 * (the per-year tooltip, the printed year inside a segment, the alternating tint that keeps
 * two adjacent years apart) means nothing to a grouped chart. Folding it into `BarsH` would
 * have been a `stacked`-style flag that changes what half the other props mean.
 *
 * What it borrows from `BarsH`, deliberately, so the panel reads as one chart set: the row
 * geometry constants (`BARSH_*`), the left label gutter with its arithmetic ellipsis, the X
 * axis with its gridlines and compact ticks, the printed value past a bar's end flipped
 * inside when there is no room, the em dash where a number is missing (§D3: absent is not
 * zero), the hovered-row highlight band, the shared `ChartTip` portal (§F2), and the
 * keyboard-reachable clickable row that fits the map (S2 item 8).
 *
 * Three things are its own:
 *
 * 1. **Alternating segment tint.** Eight years in one green is one bar. Every other segment
 *    is drawn at `SEG_ALT_OPACITY`, with a 1 px white hairline on each join, so a reader can
 *    count the years without hovering.
 * 2. **The year is printed inside its segment only when it fits** (`SEG_TEXT_MIN`). A label
 *    drawn in a 4 px segment is a label drawn over its neighbours.
 * 3. **Nothing here knows what a target is.** The colours, the row's accessible sentence and
 *    every tooltip line come from the caller, as everywhere else in this set; this file owns
 *    geometry and nothing else.
 */
import { useMemo, useState } from 'react';

import { EMDASH, fmtNum } from '../../lib/format';
import {
  BARSH_AXIS_GUTTER,
  BARSH_BAR_GAP,
  BARSH_CHAR_RATIO,
  BARSH_HEADROOM,
  BARSH_LABEL_GUTTER,
  BARSH_LABEL_SIZE,
  BARSH_PAD_RIGHT,
  BARSH_PAD_TOP,
  BARSH_ROW_GAP,
  BARSH_TICKS,
} from './BarsH';
import { hideChartTip, showChartTip } from './chartTipStore';

/** The least width a segment needs before its year is printed inside it. */
export const SEG_TEXT_MIN = 30;
/** Every other segment is drawn at this opacity, so two adjacent years are two blocks. */
export const SEG_ALT_OPACITY = 0.78;
/** The hairline between two segments — one pixel of the panel's own ground. */
export const SEG_HAIRLINE = '#FFFFFF';

/** One reported year inside a row's actuals stack. */
export interface TargetRowSegment {
  year: number;
  value: number;
  /** The tooltip's context line (`2025 actuals`). The number is formatted here. */
  tip: string;
}

export interface TargetRowDatum {
  /** Stable key and React id — the unit id, for a country row. Never shown. */
  key: string;
  /** The row's name in the left gutter — a country. */
  label: string;
  /** The end-of-programme commitment, or `null` when none is served (no bar, em dash). */
  target: number | null;
  /** The target bar's tooltip context line (`Programme target (apportioned)`). */
  targetTip: string;
  /** One per reported year, in year order. Empty draws no stack and an em dash. */
  segments: readonly TargetRowSegment[];
  /** The stack's total — cumulative achieved to date. `null` with no segments. */
  achieved: number | null;
  /** The whole row as one sentence, for a reader who cannot see it. */
  aria: string;
}

export interface TargetRowsProps {
  rows: readonly TargetRowDatum[];
  w?: number;
  /** Height of one row's whole band — both bars and the gap between them. */
  rowH?: number;
  labelGutter?: number;
  labelSize?: number;
  /** Appended to every number in every tooltip (`225,106 Farmers`). */
  unit?: string;
  /** §G3 decimals. */
  fmt?: number;
  /** X gridlines, including 0 and the maximum. Fewer than 2 draws none. */
  ticks?: number;
  /** Multiplier on the longest bar that sets the axis maximum. */
  headroom?: number;
  targetColor?: string;
  actualsColor?: string;
  ariaLabel?: string;
  /** S2 item 8: "click a bar → the map resizes to that country". Omit and rows are inert. */
  onSelect?: (index: number, row: TargetRowDatum) => void;
  className?: string;
}

/**
 * A compact axis label: `250K`, `1.2M`, `800`.
 *
 * The same register as `BarsH`'s, written again rather than imported: it is module-private
 * there on purpose (a chart's axis is not a shared formatter), and exporting a plain function
 * from a file that also exports a component costs a `react-refresh` warning for one caller.
 * It is five lines, and both copies are asserted through their own rendered tick text.
 */
function axisTick(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${Math.round((value / 1_000_000) * 10) / 10}M`;
  if (abs >= 1_000) return `${Math.round((value / 1_000) * 10) / 10}K`;
  return String(Math.round(value));
}

export function TargetRows({
  rows,
  w = 290,
  rowH = 45,
  labelGutter = BARSH_LABEL_GUTTER,
  labelSize = BARSH_LABEL_SIZE,
  unit = '',
  fmt = 0,
  ticks = BARSH_TICKS,
  headroom = BARSH_HEADROOM,
  targetColor = '#2B6CB0',
  actualsColor = '#2E7D32',
  ariaLabel,
  onSelect,
  className,
}: TargetRowsProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const chart = useMemo(() => {
    const height = BARSH_PAD_TOP + rows.length * rowH + BARSH_AXIS_GUTTER;
    const plotW = Math.max(10, w - labelGutter - BARSH_PAD_RIGHT);
    const bandH = rowH - BARSH_ROW_GAP;
    // Two bars in the band, one above the other, with the set's own gap between them.
    const barH = Math.max(3, (bandH - BARSH_BAR_GAP) / 2);
    const totals = rows.map((row) => row.segments.reduce((sum, s) => sum + Math.max(0, s.value), 0));
    const peak = Math.max(0, ...rows.map((row) => row.target ?? 0), ...totals);
    // `peak > 0` is the difference between an empty chart and a chart of NaN path data —
    // which is the state every panel opens in, before the four requests have landed.
    const max = peak > 0 ? peak * headroom : 1;

    return {
      height,
      plotW,
      max,
      bars: rows.map((row, i) => {
        const y = BARSH_PAD_TOP + i * rowH;
        const stackY = y + barH + BARSH_BAR_GAP;
        let x = labelGutter;
        const segments = row.segments.map((seg) => {
          const width = Math.max(0, (Math.max(0, seg.value) / max) * plotW);
          const laid = { seg, x, width };
          x += width;
          return laid;
        });
        return {
          row,
          index: i,
          y,
          bandH,
          barH,
          stackY,
          targetLen: row.target === null ? 0 : Math.max(0, (row.target / max) * plotW),
          segments,
          stackEnd: x,
          total: totals[i] ?? 0,
        };
      }),
    };
  }, [rows, w, rowH, labelGutter, headroom]);

  const withUnit = (v: number) => `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  const leave = () => {
    setHovered(null);
    hideChartTip();
  };
  const tipAt = (e: { clientX: number; clientY: number }, index: number, label: string, value: string) => {
    setHovered(index);
    showChartTip(e, label, value);
  };

  /**
   * The row's own popup, for a row reached by tabbing rather than pointed at.
   *
   * `showChartTip` positions from a cursor and a keyboard has none, so the row's box stands
   * in for one — which puts the tip beside the row exactly where the pointer would.
   */
  const focusRow = (node: SVGGElement, index: number) => {
    const bar = chart.bars[index];
    if (!bar) return;
    const box = node.getBoundingClientRect();
    tipAt(
      { clientX: box.right, clientY: box.top + box.height / 2 },
      index,
      bar.row.targetTip,
      bar.row.target === null ? EMDASH : withUnit(bar.row.target),
    );
  };

  /** A label that fits its gutter, or the start of one plus an ellipsis (`BarsH`'s rule). */
  const fitLabel = (label: string) => {
    const max = Math.max(3, Math.floor((labelGutter - 6) / (labelSize * BARSH_CHAR_RATIO)));
    return label.length <= max ? label : `${label.slice(0, max - 1).trimEnd()}…`;
  };

  const tickValues =
    ticks >= 2 ? Array.from({ length: ticks }, (_, i) => (chart.max / (ticks - 1)) * i) : [];

  /** Print a bar's number past its end, or inside it when the frame has no room out there. */
  const endText = (end: number, y: number, value: number | null, hook: string) => {
    const overflows = end + 30 > w;
    return (
      <text
        x={overflows ? end - 3 : end + 3}
        y={y + 3}
        textAnchor={overflows ? 'end' : 'start'}
        fontSize={8}
        fontWeight={700}
        fill={overflows ? '#FFFFFF' : '#22301F'}
        data-trow-total={hook}
      >
        {value === null ? EMDASH : fmtNum(value, fmt)}
      </text>
    );
  };

  return (
    <svg
      className={`chart trows${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${w} ${chart.height}`}
      role="img"
      aria-label={ariaLabel ?? `${rows.length} rows`}
      data-rows={rows.length}
      onMouseLeave={leave}
    >
      {/* Gridlines first, so every bar is drawn over them rather than under. */}
      {tickValues.map((value, i) => {
        const x = labelGutter + (value / chart.max) * chart.plotW;
        return (
          <g key={`t${i}`} data-trow-tick={i}>
            <line
              x1={x}
              x2={x}
              y1={BARSH_PAD_TOP}
              y2={chart.height - BARSH_AXIS_GUTTER}
              stroke="#E4E9E2"
              strokeWidth={1}
            />
            <text
              x={x}
              y={chart.height - 4}
              textAnchor={i === 0 ? 'start' : i === tickValues.length - 1 ? 'end' : 'middle'}
              fontSize={8}
              fill="#8B958A"
            >
              {axisTick(value)}
            </text>
          </g>
        );
      })}

      {chart.bars.map((bar) => {
        const dim = hovered === null || hovered === bar.index ? 1 : 0.55;
        return (
          <g
            key={bar.row.key}
            data-trow-row={bar.index}
            data-label={bar.row.label}
            data-hovered={hovered === bar.index ? '1' : undefined}
            style={onSelect ? { cursor: 'pointer' } : undefined}
            tabIndex={onSelect ? 0 : undefined}
            role={onSelect ? 'button' : undefined}
            aria-label={bar.row.aria}
            onMouseLeave={leave}
            onFocus={onSelect ? (e) => focusRow(e.currentTarget, bar.index) : undefined}
            onBlur={onSelect ? leave : undefined}
            onKeyDown={
              onSelect
                ? (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    onSelect(bar.index, bar.row);
                  }
                : undefined
            }
            onClick={onSelect ? () => onSelect(bar.index, bar.row) : undefined}
          >
            {/* The highlight band, and the row's one hit target: a reader aiming at a row in
                a 300 px panel should not have to find a 18 px bar inside it. */}
            <rect
              x={0}
              y={bar.y - BARSH_ROW_GAP / 2}
              width={w}
              height={bar.bandH + BARSH_ROW_GAP}
              fill={hovered === bar.index ? 'rgba(46,125,50,.08)' : 'transparent'}
              data-trow-band={bar.index}
            />
            <text
              x={labelGutter - 6}
              y={bar.y + bar.bandH / 2 + 3}
              textAnchor="end"
              fontSize={labelSize}
              fill="#22301F"
              data-trow-label={bar.index}
              data-label-full={bar.row.label}
            >
              {fitLabel(bar.row.label)}
            </text>

            {/* The commitment. */}
            {bar.row.target === null ? null : (
              <rect
                data-trow-bar="target"
                data-trow-value={bar.row.target}
                x={labelGutter}
                y={bar.y}
                width={bar.targetLen}
                height={bar.barH}
                rx={2}
                fill={targetColor}
                opacity={dim}
                onMouseEnter={(e) =>
                  tipAt(e, bar.index, bar.row.targetTip, withUnit(bar.row.target ?? 0))
                }
                onMouseMove={(e) =>
                  tipAt(e, bar.index, bar.row.targetTip, withUnit(bar.row.target ?? 0))
                }
              />
            )}
            {endText(labelGutter + bar.targetLen, bar.y + bar.barH / 2, bar.row.target, `${bar.index}:target`)}

            {/* The achievement, one segment per reported year. */}
            <g data-trow-bar="actuals">
              {bar.segments.map((laid, si) => (
                <rect
                  key={laid.seg.year}
                  data-trow-seg={laid.seg.year}
                  data-trow-value={laid.seg.value}
                  x={laid.x}
                  y={bar.stackY}
                  width={laid.width}
                  height={bar.barH}
                  fill={actualsColor}
                  opacity={dim * (si % 2 === 0 ? 1 : SEG_ALT_OPACITY)}
                  onMouseEnter={(e) => tipAt(e, bar.index, laid.seg.tip, withUnit(laid.seg.value))}
                  onMouseMove={(e) => tipAt(e, bar.index, laid.seg.tip, withUnit(laid.seg.value))}
                />
              ))}
              {/* The joins, drawn over the segments so neither neighbour covers them. */}
              {bar.segments.slice(1).map((laid) => (
                <rect
                  key={`hair-${laid.seg.year}`}
                  x={laid.x}
                  y={bar.stackY}
                  width={1}
                  height={bar.barH}
                  fill={SEG_HAIRLINE}
                  pointerEvents="none"
                />
              ))}
              {bar.segments
                .filter((laid) => laid.width >= SEG_TEXT_MIN)
                .map((laid) => (
                  <text
                    key={`y-${laid.seg.year}`}
                    x={laid.x + laid.width / 2}
                    y={bar.stackY + bar.barH / 2 + 3}
                    textAnchor="middle"
                    fontSize={9}
                    fill="#FFFFFF"
                    pointerEvents="none"
                    data-trow-segtext={laid.seg.year}
                  >
                    {laid.seg.year}
                  </text>
                ))}
            </g>
            {endText(bar.stackEnd, bar.stackY + bar.barH / 2, bar.row.achieved, `${bar.index}:actuals`)}
          </g>
        );
      })}
    </svg>
  );
}

export default TargetRows;
